import React, { useState } from 'react';

export default function FinancialDataModal({ onSubmit, onClose, isOpen }) {
  const [inputs, setInputs] = useState({
    preTaxProfit: 1500000,
    growthTrend: '',
    recurringRevenue: 50,
  });
  const [errors, setErrors] = useState({});

  // Format number with commas
  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  // Get profit range label
  const getProfitRangeLabel = (value) => {
    if (value < 500000) return 'Early Stage';
    if (value < 2000000) return 'Growing Business';
    if (value < 5000000) return 'Established Business';
    if (value < 10000000) return 'Market Leader';
    return 'Enterprise';
  };

  const updateInput = (field, value) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateInputs = () => {
    const newErrors = {};

    if (!inputs.preTaxProfit || inputs.preTaxProfit < 100000) {
      newErrors.preTaxProfit = 'Please select your annual profit';
    }

    if (!inputs.growthTrend) {
      newErrors.growthTrend = 'Please select your growth trend';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateInputs()) {
      onSubmit(inputs);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60 transition-opacity backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full transform transition-all animate-scale-in">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Header */}
          <div className="p-8 pb-0">
            <div className="text-center mb-6">
              <div className="mb-4">
                <span className="bg-gradient-to-r from-blue-50 to-emerald-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                  Quick Financial Info
                </span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Let's Calculate Your <span className="text-gradient">Valuation</span>
              </h2>
              <p className="text-gray-600">
                We just need 3 quick details to give you an accurate business valuation
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 pt-6">
            <div className="space-y-6">
              {/* Pre-tax Profit */}
              <div>
                <label htmlFor="preTaxProfit" className="block text-lg font-semibold text-gray-900 mb-3">
                  1. What's your annual pre-tax profit?
                </label>
                
                {/* Display Value */}
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-blue-600 mb-2">
                    ${formatNumber(inputs.preTaxProfit)}
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    {getProfitRangeLabel(inputs.preTaxProfit)}
                  </div>
                </div>

                {/* Slider */}
                <div className="relative mb-4">
                  <input
                    id="preTaxProfit"
                    type="range"
                    min="100000"
                    max="10000000"
                    step="50000"
                    value={inputs.preTaxProfit}
                    onChange={(e) => updateInput('preTaxProfit', e.target.value)}
                    className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${
                        ((inputs.preTaxProfit - 100000) / (10000000 - 100000)) * 100
                      }%, #E5E7EB ${
                        ((inputs.preTaxProfit - 100000) / (10000000 - 100000)) * 100
                      }%, #E5E7EB 100%)`,
                    }}
                  />
                  
                  {/* Markers */}
                  <div className="flex justify-between mt-3 px-1">
                    {[100000, 2500000, 5000000, 7500000, 10000000].map((value) => (
                      <div key={value} className="flex flex-col items-center">
                        <div
                          className={`w-1 h-1 rounded-full ${
                            inputs.preTaxProfit >= value ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        ></div>
                        <span className="text-xs text-gray-400 mt-1">
                          ${value >= 1000000 ? `${value / 1000000}M` : `${value / 1000}K`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Select Buttons */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {[
                    { value: 500000, label: '$500K' },
                    { value: 1500000, label: '$1.5M' },
                    { value: 3000000, label: '$3M' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateInput('preTaxProfit', option.value)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        inputs.preTaxProfit === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {errors.preTaxProfit && (
                  <p className="text-red-500 text-sm mt-2">{errors.preTaxProfit}</p>
                )}
              </div>

              {/* Growth Trend */}
              <div>
                              <div className="block text-lg font-semibold text-gray-900 mb-3">
                2. How's your business trending?
              </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'increasing', label: 'Growing', icon: '📈', color: 'emerald' },
                    { value: 'flat', label: 'Stable', icon: '➡️', color: 'blue' },
                    { value: 'decreasing', label: 'Declining', icon: '📉', color: 'orange' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateInput('growthTrend', option.value)}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                        inputs.growthTrend === option.value
                          ? option.value === 'increasing'
                            ? 'border-emerald-500 bg-emerald-50'
                            : option.value === 'flat'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-orange-500 bg-orange-50'
                          : errors.growthTrend
                            ? 'border-red-200 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{option.icon}</div>
                      <div className="font-medium text-gray-900">{option.label}</div>
                    </button>
                  ))}
                </div>
                {errors.growthTrend && (
                  <p className="text-red-500 text-sm mt-2">{errors.growthTrend}</p>
                )}
              </div>

              {/* Recurring Revenue */}
              <div>
                              <label htmlFor="recurringRevenue" className="block text-lg font-semibold text-gray-900 mb-3">
                3. What % of revenue is recurring?
              </label>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-blue-600 mb-1">
                    {inputs.recurringRevenue}%
                  </div>
                  <div className="text-sm text-gray-600">of total revenue</div>
                </div>
                <div className="relative mb-6">
                  <input
                    id="recurringRevenue"
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={inputs.recurringRevenue}
                    onChange={(e) => updateInput('recurringRevenue', e.target.value)}
                    className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${inputs.recurringRevenue}%, #E5E7EB ${inputs.recurringRevenue}%, #E5E7EB 100%)`,
                    }}
                  />
                  <div className="flex justify-between mt-2 px-1">
                    {[0, 25, 50, 75, 100].map((value) => (
                      <div key={value} className="flex flex-col items-center">
                        <div
                          className={`w-1 h-1 rounded-full ${
                            inputs.recurringRevenue >= value ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        ></div>
                        <span className="text-xs text-gray-400 mt-1">{value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center p-2 rounded-lg bg-gray-50">
                    <div className="font-medium text-gray-700">Low (0-30%)</div>
                    <div className="text-gray-500">Project-based</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-blue-50">
                    <div className="font-medium text-blue-700">Medium (30-70%)</div>
                    <div className="text-gray-500">Mixed model</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-emerald-50">
                    <div className="font-medium text-emerald-700">High (70%+)</div>
                    <div className="text-gray-500">Subscription</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Calculate Valuation →
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex justify-center items-center gap-6 mt-6 text-xs text-gray-500">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-1 text-emerald-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                100% Confidential
              </div>
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-1 text-emerald-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                No Signup Required
              </div>
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-1 text-emerald-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Instant Results
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
